// ============================================================================
// src/lib/seo/site.ts
// PV-1052 · SEO lot 1 — per-route meta for the static public pages + the
// noindex meta shared by private/system routes.
//
// Copy is fixed English ON PURPOSE, not i18n keys: V1 serves a single URL per
// page with client-side language switching (no /fr/, no hreflang — CDC §16
// keeps multilingual ROUTING in V2), so each URL gets exactly one indexable
// head and EN is the indexing language. Wiring these to i18n would make the
// head flip with the visitor's language on a URL crawlers treat as one page.
// ============================================================================

import { APP } from '@/config/app';
import { SEO_ORIGIN, type SeoJsonLdEntry, type SeoMeta } from './head';

/** Site-wide fallback description — mirrors the index.html default. Exported
 *  so the spa-shell consistency test can assert the build-time neutral head
 *  (scripts/spa-shell.lib.mjs) never drifts from it. */
export const SITE_TAGLINE =
  "Distress Deal Market — Dubai's distress deals before the market. By Luxarabia.";

// WebSite entity for the home page, linked to the static RealEstateAgent
// block (index.html) through its #organization @id.
const WEBSITE_JSONLD: SeoJsonLdEntry = {
  id: 'website',
  data: {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SEO_ORIGIN}/#website`,
    url: `${SEO_ORIGIN}/`,
    name: APP.name,
    alternateName: APP.brand,
    publisher: { '@id': `${SEO_ORIGIN}/#organization` },
  },
};

// `/` IS the catalogue (ADR-018) — the home meta targets the commercial
// queries (distress deals Dubai, below market value), not a brand splash.
export const HOME_META: SeoMeta = {
  title: 'Distress Deals in Dubai | Below Market Value Properties',
  description:
    'Verified distress property deals in Dubai — ready and off-plan homes below market value, benchmarked against Dubai Land Department data. By Luxarabia.',
  canonicalPath: '/',
  jsonLd: [WEBSITE_JSONLD],
};

export const FAQ_META: SeoMeta = {
  title: `FAQ — How Distress Deals Work in Dubai | ${APP.name}`,
  description:
    'Common questions about buying distress properties in Dubai: what a distress deal is, how discounts are verified, off-plan vs ready, process and payment.',
  canonicalPath: '/faq',
};

export const ABOUT_META: SeoMeta = {
  title: `About Us — ${APP.name} | By Luxarabia`,
  description:
    "Who we are and how we work: the team and method behind Distress Deal Market, Dubai's curated distress-deal marketplace. An editorial vertical of Luxarabia.",
  canonicalPath: '/about',
};

export const CONTACT_META: SeoMeta = {
  title: `Contact Us | ${APP.name}`,
  description:
    'Contact the Distress Deal Market team in Dubai — WhatsApp, email or contact form — for questions about a deal, selling a property or working with Luxarabia.',
  canonicalPath: '/contact',
};

export const SELL_META: SeoMeta = {
  title: `Sell Your Property Fast in Dubai | ${APP.name}`,
  description:
    'Submit your Dubai property for a fast, discreet sale. The Distress Deal Market team reviews every submission and gets back to you directly.',
  canonicalPath: '/sell',
};

// Keyed on the LegalPage slugs (router.tsx) — local union rather than an
// import from the route component so lib/ never depends on routes/.
export const LEGAL_META: Record<'privacy' | 'terms' | 'cookies', SeoMeta> = {
  privacy: {
    title: `Privacy Policy | ${APP.name}`,
    description:
      'How Distress Deal Market collects, uses and protects your personal data, and the rights you can exercise on your account.',
    canonicalPath: '/legal/privacy',
  },
  terms: {
    title: `Terms & Conditions | ${APP.name}`,
    description:
      'The terms governing your use of Distress Deal Market: accounts, deal information, advisor contact and acceptable use.',
    canonicalPath: '/legal/terms',
  },
  cookies: {
    title: `Cookie Policy | ${APP.name}`,
    description:
      'Which cookies Distress Deal Market uses, what they do, and how to manage your consent preferences.',
    canonicalPath: '/legal/cookies',
  },
};

/**
 * Head for private / auth / system routes: distinct tab title, `noindex`,
 * and NO canonical (an omitted canonicalPath actively removes the tag).
 */
export function privateMeta(title: string): SeoMeta {
  return {
    title: `${title} | ${APP.name}`,
    description: SITE_TAGLINE,
    robots: 'noindex',
  };
}

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * FAQPage JSON-LD from the CMS faq_list items — only emitted when real
 * content exists (empty questions/answers filtered out, null when nothing
 * remains). Answers are CMS rich text, so tags are stripped for the
 * `acceptedAnswer.text` payload.
 */
export function buildFaqPageJsonLd(
  items: ReadonlyArray<{ question: string; answer: string }>,
): SeoJsonLdEntry | null {
  const mainEntity = items
    .map((item) => ({ question: item.question.trim(), answer: stripHtml(item.answer) }))
    .filter((item) => item.question.length > 0 && item.answer.length > 0)
    .map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    }));
  if (mainEntity.length === 0) return null;
  return {
    id: 'faq',
    data: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity,
    },
  };
}
